var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AppConfig from '../../config/config';
const SHOPIFY_GRAPHQL_URL = `https://${AppConfig.searchSettings.shopify.domain}/api/2024-10/graphql.json`;
const SEARCH_QUERY = `
  query searchProducts($query: String!, $first: Int!, $after: String, $sortKey: ProductSortKeys, $reverse: Boolean) {
    products(first: $first, after: $after, query: $query, sortKey: $sortKey, reverse: $reverse) {
      edges {
        node {
          id
          title
          handle
          vendor
          productType
          tags
          availableForSale
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 1) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          metafields(identifiers: [
            {namespace: "custom", key: "featured"},
            {namespace: "custom", key: "badge"},
            {namespace: "custom", key: "sort_priority"}
          ]) {
            namespace
            key
            value
            type
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;
const SEARCH_PRODUCTS_QUERY = `
  query searchProducts($query: String!, $first: Int!, $after: String, $productFilters: [ProductFilter!]) {
    search(
      query: $query,
      first: $first,
      after: $after,
      productFilters: $productFilters,
      types: [PRODUCT]
    ) {
      edges {
        node {
          ... on Product {
            id
            title
            handle
            vendor
            productType
            tags
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            metafields(identifiers: [
              {namespace: "custom", key: "featured"},
              {namespace: "custom", key: "badge"},
              {namespace: "custom", key: "sort_priority"}
            ]) {
              namespace
              key
              value
              type
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      productFilters {
        id
        label
        type
        values {
          id
          label
          count
        }
      }
    }
  }
`;
const PREDICTIVE_SEARCH_QUERY = `
  query predictiveSearch($query: String!, $limit: Int) {
    predictiveSearch(
      query: $query,
      limit: $limit,
      types: [PRODUCT, COLLECTION]
    ) {
      queries
      products {
        id
        title
        handle
        vendor
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        images(first: 1) {
          edges {
            node {
              url
              altText
            }
          }
        }
      }
      collections {
        id
        title
        handle
      }
    }
  }
`;
const COLLECTION_QUERY = `
  query getCollection($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean, $filters: [ProductFilter!]) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {
        edges {
          node {
            id
            title
            handle
            vendor
            productType
            tags
            availableForSale
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                }
              }
            }
            metafields(identifiers: [
              {namespace: "custom", key: "featured"},
              {namespace: "custom", key: "badge"},
              {namespace: "custom", key: "sort_priority"}
            ]) {
              namespace
              key
              value
              type
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;
function getSortParams(sort, isCollection) {
    switch (sort) {
        case 'price:asc':
            return { sortKey: 'PRICE', reverse: false };
        case 'price:desc':
            return { sortKey: 'PRICE', reverse: true };
        case 'title:asc':
            return { sortKey: 'TITLE', reverse: false };
        case 'title:desc':
            return { sortKey: 'TITLE', reverse: true };
        case 'created:desc':
            return { sortKey: 'CREATED_AT', reverse: true };
        case 'created:asc':
            return { sortKey: 'CREATED_AT', reverse: false };
        case 'best-selling':
            return { sortKey: 'BEST_SELLING', reverse: false };
        case 'manual':
            return { sortKey: isCollection ? 'MANUAL' : 'RELEVANCE', reverse: false };
        case 'collection-default':
            return { sortKey: isCollection ? 'COLLECTION_DEFAULT' : 'RELEVANCE', reverse: false };
        default:
            return { sortKey: 'RELEVANCE', reverse: false };
    }
}
function buildShopifyFilters(filters, merchandising) {
    const shopifyFilters = [];
    // Standard filters
    if (filters) {
        Object.entries(filters).forEach(([key, values]) => {
            if (key === 'price' && values.length > 0) {
                const [min, max] = values[0].split(',');
                shopifyFilters.push({
                    price: {
                        min: parseFloat(min),
                        max: parseFloat(max)
                    }
                });
            }
            else if (key === 'available') {
                shopifyFilters.push({ available: values[0] === 'true' });
            }
            else if (key === 'vendor' || key === 'productType' || key === 'tag') {
                values.forEach(value => {
                    shopifyFilters.push({ [key]: value });
                });
            }
            else if (key.startsWith('metafield.')) {
                // Handle metafield filters
                const [, namespace, metakey] = key.split('.');
                values.forEach(value => {
                    shopifyFilters.push({
                        productMetafield: {
                            namespace,
                            key: metakey,
                            value
                        }
                    });
                });
            }
        });
    }
    // Merchandising metafield filters
    if (merchandising === null || merchandising === void 0 ? void 0 : merchandising.metafieldFilters) {
        merchandising.metafieldFilters.forEach(filter => {
            shopifyFilters.push({
                productMetafield: {
                    namespace: filter.namespace,
                    key: filter.key,
                    value: filter.value
                }
            });
        });
    }
    // Featured products filter
    if (merchandising === null || merchandising === void 0 ? void 0 : merchandising.featuredOnly) {
        shopifyFilters.push({
            productMetafield: {
                namespace: 'custom',
                key: 'featured',
                value: 'true'
            }
        });
    }
    return shopifyFilters;
}
export function fetchShopify(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const { query, page, pageSize, sort, filters, collection, merchandising } = params;
        const { sortKey, reverse } = getSortParams(sort, !!collection);
        // Calculate cursor for pagination (Shopify uses cursor-based pagination)
        const after = page > 1 ? btoa(`arrayconnection:${(page - 1) * pageSize - 1}`) : null;
        // Determine sort key based on merchandising settings
        let finalSortKey = sortKey;
        if (collection && (merchandising === null || merchandising === void 0 ? void 0 : merchandising.useManualSort)) {
            finalSortKey = 'MANUAL';
        }
        const graphqlQuery = collection ? COLLECTION_QUERY : SEARCH_QUERY;
        const variables = collection ? {
            handle: collection,
            first: pageSize,
            after,
            sortKey: finalSortKey,
            reverse,
            filters: buildShopifyFilters(filters, merchandising)
        } : {
            query,
            first: pageSize,
            after,
            sortKey: finalSortKey,
            reverse
        };
        try {
            const response = yield fetch(SHOPIFY_GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': AppConfig.searchSettings.shopify.storefrontToken,
                },
                body: JSON.stringify({
                    query: graphqlQuery,
                    variables
                })
            });
            if (!response.ok) {
                throw new Error(`Shopify API error: ${response.status}`);
            }
            const data = yield response.json();
            if (data.errors) {
                throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
            }
            return data;
        }
        catch (error) {
            console.error('Shopify API error:', error);
            throw error;
        }
    });
}
export function fetchShopifyPredictiveSearch(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 10) {
        try {
            const response = yield fetch(SHOPIFY_GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': AppConfig.searchSettings.shopify.storefrontToken,
                },
                body: JSON.stringify({
                    query: PREDICTIVE_SEARCH_QUERY,
                    variables: {
                        query,
                        limit: Math.min(limit, 10) // Maximum 10 results
                    }
                })
            });
            if (!response.ok) {
                throw new Error(`Shopify Predictive Search error: ${response.status}`);
            }
            const data = yield response.json();
            if (data.errors) {
                throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
            }
            return data.data.predictiveSearch;
        }
        catch (error) {
            console.error('Shopify Predictive Search error:', error);
            throw error;
        }
    });
}
